/**
 * Get the first item that pass the test
 * by second argument function
 *
 * @param {Array} list
 * @param {Function} f
 * @return {*}
 */
export function find(list, f) {
  return list.filter(f)[0];
}

/**
 * Deep copy the given object considering circular structure.
 * This function caches all nested objects and its copies.
 * If it detects circular structure, use cached copy to avoid infinite loop.
 *
 * @param {*} obj
 * @param {Array<Object>} cache
 * @return {*}
 */
export function deepCopy(obj, cache = []) {
  // just return if obj is immutable value
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // if obj is hit, it is in circular structure
  const hit = find(cache, (c) => c.original === obj);
  if (hit) {
    return hit.copy;
  }

  const copy = Array.isArray(obj) ? [] : {};
  // put the copy into cache at first
  // because we want to refer it in recursive deepCopy
  cache.push({
    original: obj,
    copy,
  });

  Object.keys(obj).forEach((key) => {
    copy[key] = deepCopy(obj[key], cache);
  });

  return copy;
}

/**
 * forEach for object
 */
export function forEachValue(obj, fn) {
  Object.keys(obj).forEach((key) => fn(obj[key], key));
}

export function isObject(obj) {
  return obj !== null && typeof obj === "object";
}

export function isPromise(val) {
  return val && typeof val.then === "function";
}

export function assert(condition, msg) {
  if (!condition) throw new Error(`[vuex] ${msg}`);
}

export const mapSetter = (state, setters = {}) => {
  return Object.keys(state).reduce((accumulate, stateName) => {
    accumulate[stateName] = {
      get: state[stateName],
    };
    // check if setter exists
    if (setters[stateName]) {
      accumulate[stateName].set = setters[stateName];
    }

    return accumulate;
  }, {});
};

export function daysDiff(date1, date2) {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
}

export function getAge(date) {
  return Math.floor((new Date() - new Date(date).getTime()) / 3.15576e10);
}

export function padTime(n) {
  return ("0" + n).slice(-2);
}

/**
 * Adapted from fix for vue-select dropdown being hidden by parent with overflow hidden
 * here: https://github.com/Lexxus/vue-select/blob/4fb9f1b11403b4db9636d58be5ce49852362be09/src/mixins/domHelpers.js
 * @param baseElement
 * @param targetElement
 * @param aboveCssClass
 */

export function getOffset(element) {
  const DROPDOWN_EDGE_MARGIN = 30;
  let el = element.offsetParent;
  let parentEl = element.parentElement;
  let top = element.offsetTop;
  let left = element.offsetLeft;
  let maxHeight;

  while (parentEl) {
    if (el === parentEl) {
      top += el.offsetTop - el.scrollTop;
      left += el.offsetLeft;
      el = el.offsetParent;
    } else {
      top -= parentEl.scrollTop;
    }
    parentEl = parentEl.parentElement;
  }

  if (typeof window === "object") {
    maxHeight = window.innerHeight - top - DROPDOWN_EDGE_MARGIN;
  }
  top =
    element.getBoundingClientRect().top -
    document.body.getBoundingClientRect().top;

  return { top, left, maxHeight };
}

export function positionDropdown(
  baseElement,
  targetElement,
  aboveCssClass = "above",
) {
  const offset = baseElement && getOffset(baseElement);
  if (baseElement && targetElement) {
    const targetHeight = targetElement.offsetHeight;

    // move it to the <body> to avoid glitches in IE
    document.body.appendChild(targetElement);

    targetElement.style.minWidth = `${baseElement.offsetWidth}px`;
    targetElement.style.left = `${offset.left}px`;

    if (offset.maxHeight < targetHeight && offset.top > targetHeight / 2) {
      let top = offset.top - targetHeight + 1;

      if (top < 0) {
        top = 0;
        targetElement.style.maxHeight = `${offset.top}px`;
      }
      targetElement.style.top = `${top}px`;
      // baseElement.classList.add(aboveCssClass)
      targetElement.classList.add(aboveCssClass);
    } else {
      if (offset.maxHeight < targetHeight) {
        targetElement.style.maxHeight = `${offset.maxHeight}px`;
      }
      targetElement.style.top = `${offset.top + baseElement.offsetHeight - 1}px`;
      // baseElement.classList.remove(aboveCssClass)
      targetElement.classList.remove(aboveCssClass);
    }
  }
}

export function delay(timeMilliseconds) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, timeMilliseconds);
  });
}

export function waitUntil(conditionFunction) {
  return new Promise(async (resolve) => {
    for (let i = 0; i < 1000; i++) {
      if (conditionFunction()) {
        break;
      }
      await delay(100);
    }
    resolve();
  });
}

/**
 * Check if the current date is before the given date
 *
 * @param year - four-digit year eg. 2026
 * @param month - zero-indexed eg. 0 for January, 1 for February, etc.
 * @param date - day of the month eg. 15
 * @returns {boolean}
 */
export function isCurrentDateBefore(year, month, date) {
  const today = new Date();
  const cutoff = new Date(year, month, date);
  return today < cutoff;
}



// WEBPACK FOOTER //
// ./src/utils.js